import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    EntityValidations, ISpecificEntityFormProps, SaveStatus,
    IRiskAppIncident,
    RiskAppIncident,
    Contributor,
    CustomField,
    RiskAppIncidentSystem,
    RiskAppIncidentStakeholder,
    RiskAppIncidentImpactType,
    RiskAppIncidentIssue
} from '../../types';
import { DateService, LookupService, ReportingCycleService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { CrDatePicker } from '../cr/CrDatePicker';
import { CrCheckbox } from '../cr/CrCheckbox';
import useMobileDetector from '../useMobileDetector';
import { CrCommandBar } from '../cr/CrCommandBar';
import { OrbUserContext } from '../OrbUserContext';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import EvidenceList from './EV/EvidenceList';
import { CrReportingCyclePicker } from '../cr/CrReportingCyclePicker';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';


class IncidentFormValidations extends EntityValidations {
    public ReportingCycle: string = null;
}

interface IIncidentFormProps extends ISpecificEntityFormProps {
    workflowID: number;
    workflowTitle?: string;
    newTaskStageID: number;
}

export const IncidentFormPage = (props: IIncidentFormProps): React.ReactElement => {
    const { dataServices, lookupData, loadLookupData: { groups, directorates, orgLevel3s, customFieldDfns, users: { all: allUsers }, zCoreUserGroups, entityStatuses, riskAppIssues, riskAppIncidentTypes, riskAppReportingChannels, riskAppSeverities, riskAppLocations, riskAppSystems, riskAppStakeholders, riskAppImpactTypes, riskAppStages /*users: { enabled: enabledUsers }*/ } } = useContext(DataContext);
    const { userPermissions, orbConfig } = useContext(OrbUserContext);
    const { userContext } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;
    const isMobile = useMobileDetector();
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => allUsers(), [allUsers]);
    //useMemo(() => enabledUsers(), [enabledUsers]);
    useMemo(() => zCoreUserGroups(), [zCoreUserGroups]);
    useMemo(() => riskAppIssues(), [riskAppIssues]);
    useMemo(() => riskAppIncidentTypes(), [riskAppIncidentTypes]);
    useMemo(() => riskAppReportingChannels(), [riskAppReportingChannels]);
    useMemo(() => riskAppSeverities(), [riskAppSeverities]);
    useMemo(() => riskAppLocations(), [riskAppLocations]);
    useMemo(() => riskAppSystems(), [riskAppSystems]);
    useMemo(() => riskAppStakeholders(), [riskAppStakeholders]);
    useMemo(() => riskAppImpactTypes(), [riskAppImpactTypes]);
    useMemo(() => riskAppStages(), [riskAppStages]);
    useMemo(() => groups(), [groups]);
    useMemo(() => directorates(), [directorates]);
    useMemo(() => orgLevel3s(), [orgLevel3s]);
    useMemo(() => customFieldDfns(), [customFieldDfns]);


    const [showArchived, setShowArchived] = useState<boolean>(true);
    const [evidence_ListFilterText, setEvidence_ListFilterText] = useState<string>('');
    const [taskAddedOn, setTaskAddedOn] = useState<string>('');


    const validateEntity = (incident: IRiskAppIncident): Promise<IncidentFormValidations> => {
        const errors = new IncidentFormValidations();

        if (incident.Title === null || incident.Title === '') {
            errors.Title = `incident name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }
        // if (!ReportingCycleService.reportingCycleIsValid(incident)) {
        //     errors.ReportingCycle = 'Please select all values for the reporting cycle';
        //     errors.Valid = false;
        // }
        // else {
        //     errors.ReportingCycle = null;
        // }

        return Promise.resolve(errors);
    };

    const handleEvidence_ChangeFilterText = (value: string): void => {
        setEvidence_ListFilterText(value);
    }

    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'Risk Register - Common',
                'Risk Register - Incidents',
            ]
        );
    }, [lookupData.CustomFieldDfns]);

    return (
        <div className={`${styles.cr} ${styles.updatesContainer}`} style={{ width: 'calc(100% - 1px)' }}>
            <EntityForm<IRiskAppIncident, IncidentFormValidations>
                {...props}
                entityName='Incident'
                mode='page'
                renderFormFields={(changeHandlers, formState, saveEntity) => {
                    const { FormData: formData, ValidationErrors: errors, FormIsDirty, FormSaveStatus } = formState;

                    let commandBarItems: ICommandBarItemProps[] = [];

                    if (!FormIsDirty || FormSaveStatus === SaveStatus.Pending) {
                        commandBarItems.push({
                            key: 'Close',
                            text: 'Close',
                            iconProps: { iconName: 'Cancel' },
                            onClick: (ev, item) => {
                                props.onCancelled();
                            },
                        });
                    } else {
                        commandBarItems.push(
                            {
                                key: 'Save',
                                text: 'Save',
                                iconProps: { iconName: 'Save' },
                                onClick: (ev, item) => {
                                    saveEntity();
                                },
                            },
                            {
                                key: 'Cancel',
                                text: 'Cancel',
                                iconProps: { iconName: 'Cancel' },
                                onClick: (ev, item) => {
                                    props.onCancelled();
                                },
                            }
                        );
                    }

                    //Labels row 3
                    const stageLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Stage</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const severityLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Severity</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 4
                    const incidentTypeLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Type</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const dateOccuredLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Date Occured</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 5
                    const dateReportedLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Date Reported</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const reportedByLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Reported By</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 6
                    const reportingChannelLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Reporting Channel</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const targetResolutionDateLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Target Resolution Date </span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    //Labels row 5


                    const statusLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Status</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;


                    return (
                        <div>
                            {/* <CrLoadingOverlay isLoading={isLoading} opaque={true} />*/}
                            <CrCommandBar items={commandBarItems} />
                            {/* <div className='tasks-info'>
                                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                    <div className={styles.sectionATitle}>
                                        {props.entityId > 0 ? formData?.Title : 'New incident'}
                                    </div>
                                </div>
                            </div> */}

                            {/* <div className="info-table-container">
                                <table className="info-table" cellSpacing="0" cellPadding="10">
                                    <tbody>
                                        <tr>
                                            <td className="label">
                                                Workstream
                                            </td>
                                            <td className="value">
                                                {props.workflowTitle}
                                            </td>
                                            <td className="label">
                                                Added By
                                            </td>
                                            <td className="value">
                                                {props.entityId > 0
                                                    ? taskInfo?.['AddedBy']?.['Title']
                                                    : ''
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="label">
                                                Stage
                                            </td>
                                            <td className="value">
                                                {props.entityId > 0
                                                    ? taskInfo?.WorkflowStage?.Title
                                                    : workflowStages?.find(s => s.ID === props.newTaskStageID)?.Title || ''}

                                            </td>
                                            <td className="label">
                                                Added On
                                            </td>
                                            <td className="value">
                                                {taskAddedOn}
                                            </td>
                                        </tr>

                                    </tbody>
                                </table>
                            </div> */}

                            {/* Basic Details Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '10px' /*marginTop: '30px'*/ }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Basic Details</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Incident Name</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    maxLength={300}
                                                    value={formData.Title}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                                                    errorMessage={errors.Title}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2nd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Full Description</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    multiline
                                                    rows={6}
                                                    maxLength={10000}
                                                    value={formData.Description}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Description')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 3rd row */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {stageLabel}
                                            {severityLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && stageLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppStages.filter(x => x.ForIncident === true))}
                                                    selectedKey={formData.StageID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'StageID')}
                                                />
                                            </div>
                                            {isMobile && severityLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppSeverities)}
                                                    selectedKey={formData.SeverityID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'SeverityID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 4th row */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {incidentTypeLabel}
                                            {dateOccuredLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && incidentTypeLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppIncidentTypes)}
                                                    selectedKey={formData.IncidentTypeID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'IncidentTypeID')}
                                                />
                                            </div>
                                            {isMobile && dateOccuredLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.DateOccurred)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'DateOccurred')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 5th row */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {dateReportedLabel}
                                            {reportedByLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && dateReportedLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.DateReported)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'DateReported')}
                                                />
                                            </div>
                                            {isMobile && reportedByLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrUserPicker
                                                    users={lookupData.Users?.All}
                                                    selectedUsers={formData.ReportedByID && [formData.ReportedByID]}
                                                    onChange={v => changeHandlers.changeUserPicker(v, 'ReportedByID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 6th row */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {reportingChannelLabel}
                                            {targetResolutionDateLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && reportingChannelLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppReportingChannels)}
                                                    selectedKey={formData.ReportingChannelID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'ReportingChannelID')}
                                                />
                                            </div>
                                            {isMobile && targetResolutionDateLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.TargetResolutionDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'TargetResolutionDate')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 7th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Status</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                                                    selectedKey={formData.EntityStatusID}
                                                    onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {/* 8th row */}
                                    {/* <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Reports due</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                        <div style={{ display: 'flex' }}>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrReportingCyclePicker
                                                    required={true}
                                                    cycle={{ frequency: formData.ReportingFrequency, dueDay: formData.ReportingDueDay, startDate: formData.ReportingStartDate }}
                                                    onChange={cycle => changeHandlers.changeReportingCycle(cycle)}
                                                    errorMessage={errors.ReportingCycle}
                                                />
                                            </div>
                                        </div>
                                    </div> */}

                                </div>
                            </div>

                            {/* Ownership and Roles Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Ownership and Roles</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Owner Type</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions([
                                                        { ID: 0, Title: orbConfig.OrgTypeLabel },
                                                        { ID: 1, Title: orbConfig.OrgL1Label },
                                                        { ID: 2, Title: orbConfig.OrgL2Label },
                                                        { ID: 3, Title: orbConfig.OrgL3Label },
                                                    ])}
                                                    selectedKey={formData.OwnerTypeID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'OwnerTypeID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2nd row */}
                                    {formData.OwnerTypeID > 0 && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}>
                                                        {formData.OwnerTypeID === 1 && <span>{`Owner ${orbConfig.OrgL1Label}`}</span>}
                                                        {formData.OwnerTypeID === 2 && <span>{`Owner ${orbConfig.OrgL2Label}`}</span>}
                                                        {formData.OwnerTypeID === 3 && <span>{`Owner ${orbConfig.OrgL3Label}`}</span>}
                                                    </div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                {formData.OwnerTypeID === 1 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.Groups)}
                                                        selectedKey={formData.OwnerL1ID}
                                                        onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL1ID', index)}
                                                    //errorMessage={errors.OwnerL1}
                                                    />
                                                }
                                                {formData.OwnerTypeID === 2 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                                        selectedKey={formData.OwnerL2ID}
                                                        onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL2ID', index)}
                                                    //errorMessage={errors.OwnerL2}
                                                    />
                                                }
                                                {formData.OwnerTypeID === 3 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                                        selectedKey={formData.OwnerL3ID}
                                                        onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL3ID', index)}
                                                    //errorMessage={errors.OwnerL3}
                                                    />
                                                }
                                            </div>
                                        </div>
                                    </div>}

                                    {/* 3rd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Managed By Type</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions([
                                                        { ID: 2, Title: orbConfig.OrgL2Label },
                                                        { ID: 3, Title: orbConfig.OrgL3Label },
                                                    ])}
                                                    selectedKey={formData.ManagedByTypeID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'ManagedByTypeID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 4th row */}
                                    {formData.ManagedByTypeID > 1 && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}>
                                                        {formData.ManagedByTypeID === 2 && <span>{`Managed by ${orbConfig.OrgL2Label}`}</span>}
                                                        {formData.ManagedByTypeID === 3 && <span>{`Managed by ${orbConfig.OrgL3Label}`}</span>}
                                                    </div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                {formData.ManagedByTypeID === 2 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                                        selectedKey={formData.ManagedByL2ID}
                                                        onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'ManagedByL2ID', index)}
                                                    //errorMessage={errors.ManagedByL2}
                                                    />
                                                }
                                                {formData.ManagedByTypeID === 3 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                                        selectedKey={formData.ManagedByL3ID}
                                                        onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'ManagedByL3ID', index)}
                                                    //errorMessage={errors.ManagedByL3}
                                                    />
                                                }

                                            </div>
                                        </div>
                                    </div>}

                                    {/* 5th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Primary Owner</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrUserPicker
                                                    users={lookupData.Users?.All}
                                                    selectedUsers={formData.IncidentManagerID && [formData.IncidentManagerID]}
                                                    onChange={v => changeHandlers.changeUserPicker(v, 'IncidentManagerID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 6th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Contributors</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrUserPicker
                                                    users={lookupData.Users?.All}
                                                    itemLimit={3}
                                                    selectedUsers={formData.Contributors?.map(c => c.ContributorUserID)}
                                                    onChange={v => changeHandlers.changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Context and Effected Area Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Context and Effected Area</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Location</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppLocations)}
                                                    selectedKey={formData.LocationID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'LocationID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2nd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Systems or Services Effected </span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    multiSelect
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppSystems)}
                                                    selectedKeys={formData.RiskAppIncidentSystems?.map(x => x.SystemID)}
                                                    onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'RiskAppIncidentSystems', new RiskAppIncidentSystem(), 'SystemID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 3rd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Impacted Stakeholders</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    multiSelect
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppStakeholders)}
                                                    selectedKeys={formData.RiskAppIncidentStakeholders?.map(x => x.StakeholderID)}
                                                    onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'RiskAppIncidentStakeholders', new RiskAppIncidentStakeholder(), 'StakeholderID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 4th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Potential Impact Summary</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    multiSelect
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppImpactTypes)}
                                                    selectedKeys={formData.RiskAppIncidentImpactTypes?.map(x => x.ImpactTypeID)}
                                                    onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'RiskAppIncidentImpactTypes', new RiskAppIncidentImpactType(), 'ImpactTypeID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 5th row */}
                                    {/* <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Linked Risk(s)</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                        <div style={{ display: 'flex' }}>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    maxLength={300}
                                                    value={formData.Title}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                                                    errorMessage={errors.Title}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    </div> */}

                                    {/* 6th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Linked Issues(s)</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    multiSelect
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.RiskAppIssues)}
                                                    selectedKeys={formData.RiskAppIncidentIssues?.map(x => x.IssueID)}
                                                    onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'RiskAppIncidentIssues', new RiskAppIncidentIssue(), 'IssueID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 4th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Additional Information</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrCustomFields
                                                    customFieldDfns={filteredCustomFieldDfns}
                                                    selectedItems={LookupService.customFieldsToDropdownWithText(formData.CustomFields)}
                                                    onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Response and Investigation Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Response and Investigation</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Initial Response Actions</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    multiline
                                                    rows={6}
                                                    maxLength={10000}
                                                    value={formData.InitialResponseActions}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'InitialResponseActions')}
                                                />
                                            </div>
                                        </div>
                                    </div>


                                    {/* 2nd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Investigation Summary</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    multiline
                                                    rows={6}
                                                    maxLength={10000}
                                                    value={formData.InvestigationSummary}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'InvestigationSummary')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 3rd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Final Resolution Summary </span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    multiline
                                                    rows={6}
                                                    maxLength={10000}
                                                    value={formData.FinalResolutionSummary}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'FinalResolutionSummary')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 4th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Lessons Learned</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    multiline
                                                    rows={6}
                                                    maxLength={10000}
                                                    value={formData.LessonsLearned}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'LessonsLearned')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>


                        </div>
                    );
                }}
                loadEntity={id => dataServices.riskAppIncidentService.read(id, true, true)}
                onAfterLoad={(incident) => {
                    //setShowArchived(task.Archived === true ? false : true);
                }}
                loadNewEntity={() => new RiskAppIncident()}
                loadEntityValidations={() => new IncidentFormValidations()}
                onValidateEntity={validateEntity}
                onCreate={incident => dataServices.riskAppIncidentService.create(incident)}
                onUpdate={incident => dataServices.riskAppIncidentService.update(incident.ID, incident)}
                parentEntities={dataServices.riskAppIncidentService.parentEntities}
                childEntities={[
                    {
                        ObjectParentProperty: 'Contributors',
                        ParentIdProperty: 'RiskAppIncidentID',
                        ChildIdProperty: 'ContributorUserID',
                        ChildService: dataServices.contributorService
                    },
                    {
                        ObjectParentProperty: 'CustomFields',
                        ParentIdProperty: 'RiskAppIncidentID',
                        ChildIdProperty: 'CustomFieldDefnID',
                        ChildService: dataServices.customFieldService
                    },
                    {
                        ObjectParentProperty: 'RiskAppIncidentSystems',
                        ParentIdProperty: 'IncidentID',
                        ChildIdProperty: 'SystemID',
                        ChildService: dataServices.riskAppIncidentSystemService
                    },
                    {
                        ObjectParentProperty: 'RiskAppIncidentStakeholders',
                        ParentIdProperty: 'IncidentID',
                        ChildIdProperty: 'StakeholderID',
                        ChildService: dataServices.riskAppIncidentStakeholderService
                    },
                    {
                        ObjectParentProperty: 'RiskAppIncidentImpactTypes',
                        ParentIdProperty: 'IncidentID',
                        ChildIdProperty: 'ImpactTypeID',
                        ChildService: dataServices.riskAppIncidentImpactTypeService
                    },
                    {
                        ObjectParentProperty: 'RiskAppIncidentIssues',
                        ParentIdProperty: 'IncidentID',
                        ChildIdProperty: 'IssueID',
                        ChildService: dataServices.riskAppIncidentIssueService
                    },
                ]}
            // childEntities={[
            //     {
            //         ObjectParentProperty: 'App03TaskUsers',
            //         ParentIdProperty: 'TaskID',
            //         ChildIdProperty: 'UserID',
            //         ChildService: dataServices.app03TaskUserService
            //     },
            // ]}

            //onAfterCreate={t => dataServices.emailNotificationsService.sendEmailOnNewWorkflowTask(t.ID)}
            //onAfterUpdate2={t => dataServices.emailNotificationsService.sendEmailOnEditWorkflowTask(t.ID)}

            />
            {props.entityId > 0 &&
                <>
                    <div>
                        <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                            <div style={{ display: 'flex' }}>
                                <div className={styles.sectionATitle}>Discussion, General Comments and File Attachments</div>
                            </div>
                        </div>
                    </div>
                    <div>

                        <div style={{ minHeight: '120px' }}>
                            <EvidenceList
                                evidenceFor='Incident'
                                incidentID={props.entityId}
                                issueID={null}
                                incidentActionID={null}
                                issueActionID={null}
                                riskID={null}
                                riskMitigationActionID={null}
                                currentUserId={currentUserId}
                                superUserPermission={userPermissions.UserIsSystemAdmin()}
                                isViewOnly={false}
                                filterText={evidence_ListFilterText}
                                onChangeFilterText={handleEvidence_ChangeFilterText}
                                evChangesCounter={1}
                                {...props}
                                errorHandling={props.errorHandling}
                            />
                        </div>

                    </div>
                </>
            }

        </div>
    );
};

